/**
 * Minimap: the whole diagram at a glance, with the current viewport drawn on
 * top. Clicking or dragging inside it recentres the main view.
 *
 * Nodes are drawn as plain rectangles — at minimap scale a diamond and a box
 * are indistinguishable anyway, and skipping the real geometry keeps this cheap
 * enough to render on every pan frame.
 */
import { memo, useCallback, useRef } from 'react';
import type { FlowGraph, Point } from '../graph/model/types';
import type { Viewport } from './useViewport';

export interface MinimapProps {
  readonly graph: FlowGraph;
  readonly viewport: Viewport;
  readonly containerWidth: number;
  readonly containerHeight: number;
  readonly onNavigate: (graphPoint: Point) => void;
  readonly label: string;
}

const WIDTH = 168;
const HEIGHT = 116;
const PADDING = 6;

function MinimapImpl({
  graph,
  viewport,
  containerWidth,
  containerHeight,
  onNavigate,
  label,
}: MinimapProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const dragging = useRef(false);
  const bounds = graph.bounds;

  const navigate = useCallback(
    (clientX: number, clientY: number) => {
      const svg = svgRef.current;
      if (!svg || !bounds || bounds.width <= 0 || bounds.height <= 0) return;
      const rect = svg.getBoundingClientRect();
      const scale = Math.min(
        (WIDTH - PADDING * 2) / bounds.width,
        (HEIGHT - PADDING * 2) / bounds.height,
      );
      const offsetX = (WIDTH - bounds.width * scale) / 2;
      const offsetY = (HEIGHT - bounds.height * scale) / 2;

      onNavigate({
        x: (clientX - rect.left - offsetX) / scale + bounds.minX,
        y: (clientY - rect.top - offsetY) / scale + bounds.minY,
      });
    },
    [bounds, onNavigate],
  );

  if (!bounds || bounds.width <= 0 || bounds.height <= 0) return null;

  const scale = Math.min(
    (WIDTH - PADDING * 2) / bounds.width,
    (HEIGHT - PADDING * 2) / bounds.height,
  );
  const offsetX = (WIDTH - bounds.width * scale) / 2;
  const offsetY = (HEIGHT - bounds.height * scale) / 2;

  // The visible window, expressed in graph space then scaled into the minimap.
  const view = {
    x: -viewport.x / viewport.scale,
    y: -viewport.y / viewport.scale,
    width: containerWidth / viewport.scale,
    height: containerHeight / viewport.scale,
  };

  return (
    <svg
      ref={svgRef}
      width={WIDTH}
      height={HEIGHT}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      role="img"
      aria-label={label}
      className="rounded-lg border border-border bg-surface/90 shadow-panel backdrop-blur-sm"
      style={{ cursor: 'crosshair', touchAction: 'none' }}
      onPointerDown={(event) => {
        dragging.current = true;
        event.currentTarget.setPointerCapture(event.pointerId);
        navigate(event.clientX, event.clientY);
      }}
      onPointerMove={(event) => {
        if (dragging.current) navigate(event.clientX, event.clientY);
      }}
      onPointerUp={(event) => {
        dragging.current = false;
        event.currentTarget.releasePointerCapture(event.pointerId);
      }}
    >
      <g
        transform={`translate(${offsetX} ${offsetY}) scale(${scale}) translate(${-bounds.minX} ${-bounds.minY})`}
      >
        {graph.nodes.map((node) => (
          <rect
            key={node.id}
            x={node.position.x}
            y={node.position.y}
            width={node.size.width}
            height={node.size.height}
            rx={4 / scale}
            fill={
              node.role === 'decision' || node.role === 'loop'
                ? 'var(--fs-node-decision-stroke)'
                : 'var(--fs-border-strong)'
            }
          />
        ))}
        <rect
          x={view.x}
          y={view.y}
          width={view.width}
          height={view.height}
          fill="var(--fs-selection)"
          fillOpacity={0.12}
          stroke="var(--fs-selection)"
          strokeWidth={1.5 / scale}
        />
      </g>
    </svg>
  );
}

export const Minimap = memo(MinimapImpl);
